import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('arrise_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('arrise_token');
      localStorage.removeItem('arrise_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Designer APIs
export const designerAPI = {
  getAll: () => api.get('/designers'),
  getAllIncludingInactive: () => api.get('/designers/all'),
  getById: (id) => api.get(`/designers/${id}`),
  create: (data) => api.post('/designers', data),
  update: (id, data) => api.put(`/designers/${id}`, data),
  delete: (id) => api.delete(`/designers/${id}`),
};

// Score APIs
export const scoreAPI = {
  getAll: (params) => api.get('/scores', { params }),
  create: (data) => api.post('/scores', data),
  update: (id, data) => api.put(`/scores/${id}`, data),
  delete: (id) => api.delete(`/scores/${id}`),
};

// Performance APIs
export const performanceAPI = {
  getMonthly: (year, month) => api.get('/performance/monthly', { params: { year, month } }),
  getDesigner: (designerId, year) => api.get(`/performance/designer/${designerId}`, { params: { year } }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getAlerts: () => api.get('/dashboard/alerts'),
  getTrends: (year) => api.get('/dashboard/trends', { params: { year } }),
  getTeamBreakdown: () => api.get('/dashboard/team-breakdown'),
  getStackRanking: (params) => api.get('/dashboard/stack-ranking', { params }),
  getRoleBreakdown: () => api.get('/dashboard/role-breakdown'),
};

// Org Chart APIs
export const orgChartAPI = {
  getAll: () => api.get('/org-charts'),
  upload: (file, category) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    return api.post('/org-charts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (filename) => api.delete(`/org-charts/${filename}`),
};

// Screenshot APIs
export const screenshotAPI = {
  upload: (file, description) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    return api.post('/screenshots/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: (designerId) => api.get('/screenshots', { params: { designer_id: designerId } }),
  delete: (id) => api.delete(`/screenshots/${id}`),
};

// Targets API
export const targetsAPI = {
  getAll: () => api.get('/targets'),
  getPerformanceScale: () => api.get('/performance-scale'),
  getRoleResponsibilities: () => api.get('/role-responsibilities'),
};

// Role Responsibilities API
export const rolesAPI = {
  getAll: () => api.get('/role-responsibilities'),
  create: (data) => api.post('/role-responsibilities', data),
  update: (roleKey, data) => api.put(`/role-responsibilities/${encodeURIComponent(roleKey)}`, data),
  delete: (roleKey) => api.delete(`/role-responsibilities/${encodeURIComponent(roleKey)}`),
  addResponsibility: (roleKey, responsibility) => 
    api.post(`/role-responsibilities/${encodeURIComponent(roleKey)}/responsibility`, { responsibility }),
  deleteResponsibility: (roleKey, index) => 
    api.delete(`/role-responsibilities/${encodeURIComponent(roleKey)}/responsibility/${index}`),
};

// Achievements API
export const achievementsAPI = {
  getCategories: () => api.get('/achievements/categories'),
  getAll: (params) => api.get('/achievements', { params }),
  getPendingReview: () => api.get('/achievements/pending-review'),
  getById: (id) => api.get(`/achievements/${id}`),
  create: (data) => api.post('/achievements', data),
  update: (id, data) => api.put(`/achievements/${id}`, data),
  delete: (id) => api.delete(`/achievements/${id}`),
  uploadScreenshot: (achievementId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/achievements/${achievementId}/screenshots`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteScreenshot: (achievementId, screenshotId) => 
    api.delete(`/achievements/${achievementId}/screenshots/${screenshotId}`),
  // Approval workflow
  approve: (id, feedback) => api.patch(`/achievements/${id}/approve`, { feedback }),
  requestRevision: (id, feedback) => api.patch(`/achievements/${id}/request-revision`, { feedback }),
  reject: (id, feedback) => api.patch(`/achievements/${id}/reject`, { feedback }),
  resubmit: (id, data) => api.patch(`/achievements/${id}/resubmit`, data),
};

// Goals API - Enhanced with progress tracking
export const goalsAPI = {
  getAll: (designerId) => api.get('/goals', { params: { designer_id: designerId } }),
  getDashboard: () => api.get('/goals/dashboard'),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`),
  addComment: (id, comment) => api.post(`/goals/${id}/comment`, { comment }),
};

// 1-on-1 Notes API
export const notesAPI = {
  getAll: (designerId) => api.get('/notes', { params: { designer_id: designerId } }),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
};

// Bulk Import API
export const importAPI = {
  getTemplate: (type) => api.get(`/import/template/${type}`),
  importDesigners: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/designers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importScores: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/scores', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export default api;
